/**
 * Innloggingsskjerm
 */

import React, { useState } from 'react';
import { Trophy, Zap } from 'lucide-react';
import { signInWithGoogle } from '../services/auth';
import { PrivacyConsentModal } from './PrivacyConsentModal';

export const LoginScreen = () => {
  const [showConsent, setShowConsent] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Kunne ikke logge inn: ' + error.message);
    }
  };

  const handleLoginClick = () => {
    setShowConsent(true);
  };

  const handleAcceptConsent = () => {
    setShowConsent(false);
    handleGoogleSignIn();
  };

  const handleDeclineConsent = () => {
    setShowConsent(false);
    // User declined, do nothing
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-600 rounded-[2rem] shadow-xl shadow-orange-200 mb-6">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-black text-slate-800 tracking-tight mb-2">
            LØPDA
          </h1>
          <p className="text-slate-500 font-bold text-sm">
            Sosial løpeglede for bedrifter og vennegrupper
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white p-8 rounded-[3rem] shadow-2xl shadow-orange-100/40 border border-orange-50">
          <h2 className="text-2xl font-black text-slate-800 text-center mb-6">
            Kom i gang
          </h2>

          <button
            onClick={handleLoginClick}
            className="w-full py-4 bg-white border-2 border-slate-200 rounded-2xl font-bold text-slate-700 hover:border-orange-400 hover:bg-orange-50 transition-all flex items-center justify-center gap-3 shadow-sm"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Fortsett med Google
          </button>
        </div>

        {/* Features */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
              <Trophy className="w-6 h-6 text-orange-600" />
            </div>
            <p className="text-xs font-bold text-slate-600">
              Konkurranser
            </p>
          </div>
          <div>
            <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
              <Zap className="w-6 h-6 text-orange-600" />
            </div>
            <p className="text-xs font-bold text-slate-600">
              Leaderboard
            </p>
          </div>
          <div>
            <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
              <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
              </svg>
            </div>
            <p className="text-xs font-bold text-slate-600">
              Chat
            </p>
          </div>
        </div>

        {/* Privacy Policy Link */}
        <div className="mt-6 text-center">
          <a
            href="/PERSONVERN.md"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-slate-500 hover:text-orange-600 underline"
          >
            Les vår personvernerklæring
          </a>
        </div>
      </div>

      {/* Consent Modal */}
      {showConsent && (
        <PrivacyConsentModal
          onAccept={handleAcceptConsent}
          onDecline={handleDeclineConsent}
        />
      )}
    </div>
  );
};
